import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import MuiAlert from "@material-ui/lab/Alert";

import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import LinkIcon from "@material-ui/icons/Link";
import LinkOffIcon from "@material-ui/icons/LinkOff";

import {
  fetchPaymentAccounts,
  deletePaymentAccount,
} from "../../redux/features/hotels/paymentAccountsSlice";
import LinkStripeAccountDialog from "./LinkStripeAccountDialog";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(3, 0),
    textAlign: "center",
  },
  accountId: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
}));

const StripeAccountsSection = ({ hotelId, condoId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { accountsList, isLoading, isDeleting, error } = useSelector(
    (s) => s.paymentAccounts
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (hotelId || condoId) {
      dispatch(fetchPaymentAccounts({ hotelId, condoId }));
    }
  }, [dispatch, hotelId, condoId]);

  const handleSuccess = () => {
    setDialogOpen(false);
    setSnackbar({
      open: true,
      message: "Stripe account linked successfully!",
      severity: "success",
    });
  };

  const handleUnlinkConfirm = async () => {
    if (!unlinkTarget) return;
    const name = unlinkTarget.stripe_account_name;
    const result = await dispatch(
      deletePaymentAccount(unlinkTarget.account_id)
    );
    setUnlinkTarget(null);
    if (!result.error) {
      setSnackbar({
        open: true,
        message: `"${name}" has been unlinked.`,
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message: result.errorMessage || "Failed to unlink account.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <div className={classes.header}>
            <div className={classes.titleRow}>
              <AccountBalanceIcon color="action" />
              <Typography variant="h6">Stripe Payment Accounts</Typography>
            </div>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<LinkIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Link to a Stripe Account
            </Button>
          </div>

          <Divider />

          {isLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={28} />
            </div>
          ) : error ? (
            <Typography color="error" style={{ marginTop: 16 }}>
              {error.errorMessage || "Failed to load accounts."}
            </Typography>
          ) : accountsList.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                No accounts associated yet. If you want to do payouts you need
                to associate one account.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => setDialogOpen(true)}
                style={{ marginTop: 8 }}
              >
                Link Stripe Account
              </Button>
            </div>
          ) : (
            <List disablePadding>
              {accountsList.map((account, i) => (
                <React.Fragment key={account.account_id}>
                  {i > 0 && <Divider component="li" />}
                  <ListItem>
                    <ListItemIcon>
                      <AccountBalanceIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={account.stripe_account_name}
                      secondary={
                        <>
                          <span className={classes.accountId}>
                            {account.stripe_account_id}
                          </span>
                          {account.linked_property_name && (
                            <span>
                              {" · "}
                              {account.linked_property_name}
                            </span>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<LinkOffIcon />}
                        onClick={() => setUnlinkTarget(account)}
                        disabled={isDeleting}
                        style={{ backgroundColor: "#d32f2f", color: "#fff" }}
                      >
                        Unlink
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Grid>

      <Dialog
        open={!!unlinkTarget}
        onClose={() => !isDeleting && setUnlinkTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          disableTypography
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <LinkOffIcon color="error" />
          <Typography variant="h6">Unlink Stripe Account</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Are you sure you want to unlink{" "}
            <strong>{unlinkTarget?.stripe_account_name}</strong>?
          </Typography>
          {unlinkTarget?.stripe_account_id && (
            <Typography
              variant="caption"
              style={{
                fontFamily: "monospace",
                display: "block",
                marginTop: 8,
              }}
              color="textSecondary"
            >
              {unlinkTarget.stripe_account_id}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 12 }}
          >
            This will not affect existing payouts or transfers. The account can
            be re-linked at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkTarget(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "#d32f2f", color: "#fff" }}
            onClick={handleUnlinkConfirm}
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LinkOffIcon />
              )
            }
          >
            {isDeleting ? "Unlinking…" : "Unlink Account"}
          </Button>
        </DialogActions>
      </Dialog>

      <LinkStripeAccountDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        hotelId={hotelId}
        condoId={condoId}
        onSuccess={handleSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity || "success"}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default StripeAccountsSection;
